import { useMutation, useQueryClient } from 'react-query';
import { PostStatus } from '@kontentino/kontentino-constants/Posts';
import { ApiClientError } from 'api/client';
import Logger from 'utils/logger';
import { usePostOverviewOptimisticQueryUpdater } from 'app/modules/posts/modules/post/hooks/usePostOverview';
import { useToast } from 'app/hooks/useToast';
import CommentsApi from 'app/modules/comments/api';
import PostApi from 'api/post';
import { useTranslation } from 'react-i18next';
import { CommentAction, CommentThread } from 'app/modules/comments/types';
import { CommentConvertor } from 'app/modules/comments/utils/commentConvertor';
import { queryKey } from 'constants/queryKey';
import { useUser } from 'modules/user/userSelector';
import { modalRoutes } from 'constants/routes';
import { useModalHistory } from 'context/ModalRouter';
import { getUserFirstTimeFlagStatus } from 'utils/user/firstTimeFlags';
import { FirstTimeFlagKey } from 'types/User';
import { useDispatch } from 'react-redux';
import { UserActions } from 'modules/user/userActions';
import { useLocation } from 'react-router-dom';
import routes from 'constants/routes';
import { useContext } from 'react';
import {
  ApprovedPostsContext,
  ApprovedPost,
} from 'app/modules/approvals/context/ApprovedPostsContext';
import { PostDetail } from 'types/PostDetail';
import { getAttachmentCardThumbnail } from 'utils/attachments';

export function useUpdateApprovalPostStatus(payload: {
  postId: number;
  commentThread?: string;
}) {
  const queryClient = useQueryClient();

  const toast = useToast();
  const updater = usePostOverviewOptimisticQueryUpdater(payload.postId);
  const { t } = useTranslation();
  const user = useUser();
  const modalHistory = useModalHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const isOnApprovalsPage = location.pathname.startsWith(
    routes.POSTS_APPROVALS,
  );
  const approvedPostsContext = useContext(ApprovedPostsContext);

  const commonOptions = {
    onError(e: ApiClientError) {
      toast(e.userMessage ?? t('somethingWentWrongTryAgain'), 'error');
      Logger.error(e);
    },
    onSettled() {
      updater.invalidateData({
        commentThread: payload.commentThread,
      });
    },
  };

  const getCelebrationModalFlag = (thread: 'client' | 'internal') => {
    return thread === 'client'
      ? FirstTimeFlagKey.FIRST_CLIENT_APPROVAL_SUBMITTED
      : FirstTimeFlagKey.FIRST_INTERNAL_APPROVAL_SUBMITTED;
  };

  const showCelebrationModal = (thread: 'client' | 'internal') => {
    const flagKey = getCelebrationModalFlag(thread);
    const status = getUserFirstTimeFlagStatus(user.firstTimeFlags, flagKey);
    if (status === false) {
      modalHistory.push({ hash: modalRoutes.FIRST_POST_APPROVED });
      dispatch(UserActions.setFirstTimeFlag(flagKey));
    }
  };

  const addToApprovedPosts = (approvedPostData: ApprovedPost | null) => {
    if (!isOnApprovalsPage || !approvedPostsContext || !approvedPostData) {
      return;
    }

    approvedPostsContext.addApprovedPost(approvedPostData);
  };

  const updateOptimistically = (status: PostStatus) => {
    updater.update(() => updater.transformers.postStatus(status));

    updater.invalidateData({
      commentThread: payload.commentThread,
    });

    toast(
      `${t('post_one')} ${t(`workflowStatusMovedTo.${status}`)}`,
      'success',
    );
  };

  const updateManually = useMutation(
    ({ status, markAsDone }: { status: PostStatus; markAsDone?: boolean }) =>
      PostApi.editPostStatus({ id: payload.postId, status, markAsDone }),
    {
      onMutate({ status }) {
        updater.update(() => updater.transformers.postStatus(status));
        queryClient.invalidateQueries(queryKey.postsMeta());
        toast(
          `${t('post_one')} ${t(`workflowStatusMovedTo.${status}`)}`,
          'success',
        );
      },
      ...commonOptions,
    },
  );

  const updateThroughComment = useMutation(
    (comment: {
      text: string;
      thread: CommentThread;
      action: CommentAction;
      fileAttachments?: File[];
      approvedPostData?: ApprovedPost;
    }) => {
      const { approvedPostData: _, ...commentData } = comment;
      return CommentsApi.create({
        ...commentData,
        postId: payload.postId,
      });
    },
    {
      onMutate(comment) {
        const postData = queryClient.getQueryData<PostDetail>(
          queryKey.postDetail(payload.postId),
        );
        const previousStatus = postData?.status;

        let approvedPostData: ApprovedPost | null =
          comment.approvedPostData ?? null;

        if (
          !approvedPostData &&
          postData &&
          previousStatus &&
          (comment.thread === 'client' || comment.thread === 'internal')
        ) {
          const firstCard = postData.attachments?.cards?.[0];
          const thumbnail =
            postData.preview_image?.src ??
            (firstCard ? getAttachmentCardThumbnail(firstCard) : null) ??
            null;

          approvedPostData = {
            id: payload.postId,
            title: postData.text || '',
            thumbnail,
            previousStatus,
            thread: comment.thread,
            pageId: postData.page.id,
            pageType: postData.page.type,
          };
        }

        const status = CommentConvertor.commentActionToStatus(comment.action);
        updater.update(() => updater.transformers.postStatus(status));
        queryClient.invalidateQueries(queryKey.postsMeta());

        toast(
          `${t('post_one')} ${t(`workflowStatusMovedTo.${status}`)}`,
          'success',
        );

        return { previousStatus, approvedPostData };
      },
      onSuccess(_, comment, context) {
        if (
          comment.action === 'client_approval' ||
          comment.action === 'internal_approval'
        ) {
          if (comment.thread === 'client' || comment.thread === 'internal') {
            showCelebrationModal(comment.thread);
            addToApprovedPosts(context?.approvedPostData ?? null);
          }
        }
      },
      ...commonOptions,
    },
  );

  return {
    updateManually,
    updateThroughComment,
    updateOptimistically,
  };
}
